import { ApiResponse } from '@entities/types';

export interface UserPermission {
  id: string;
  permissionName: string;
  action: AdminLinkActionType[];
  linkId: number;
  groupId: string;
}

export type UserPermissionResponse = ApiResponse<{
  ADMIN_LINKS: UserPermission[];
  ENHANCED_SERVICE: UserPermission[];
  FSCC: UserPermission[];
  CONTROLS: UserPermission[];
}>;

export type AdminLinkActionType = ADMIN_LINKS_ACTIONS;

export enum ADMIN_LINKS_ACTIONS {
  ALL = 'ALL',
  ADD = 'ADD',
  EDIT = 'EDIT',
  ARCHIVE = 'ARCHIVE',
  FOLDER_SEARCH = 'FOLDER_SEARCH',
  RENAME = 'RENAME',
  DELETE = 'DELETE',
  SEARCH = 'SEARCH',
  DETACH = 'DETACH',
  ATTACH = 'ATTACH',
  EMAIL = 'EMAIL',
  AUTO_MATCH = 'AUTO_MATCH',
  DOWNLOAD_DOCUMENT = 'DOWNLOAD_DOCUMENT',
  CLASSIFICATION = 'CLASSIFICATION',
  UPLOAD_REPLACE_STATUS_LIST = 'UPLOAD_REPLACE_STATUS_LIST',

  // PACK
  CLONE = 'CLONE',
  COMPILE = 'COMPILE',
  SHARE = 'SHARE',
  RELOAD = 'RELOAD',
  DOWNLOAD_PACK = 'DOWNLOAD_PACK',

  // UPLOAD MANAGER
  UPLOAD = 'UPLOAD',
  MANAGE = 'MANAGE', // manage upload access (SFTP/EMAIL settings, email tag)

  // AUTO_ATTACH
  MATCH = 'MATCH',
}

export const UserPermissionLinks = {
  ENABLE_FSCC_OPTION: 5038,
  ALLOW_USER_ACCESS_RESTRICTED_DATA: 5039,
  ALLOW_MULTIPLE_ACCESS: 5040,
  ALLOW_ACCESS_PRIVATE_NOTE: 5041,
  IP_RESTRICTION: 5042,
  READ_SIGN_ADMINISTRATOR: 5043,
  ALLOW_USER_FLYSIGN: 5044,
  HIDE_RESTRICTED_COLUMNS: 5045,
  MANAGEMENT_LEVEL: 5046,
  MANAGE_ACCESS_TO_ASSETS: 5047,
  MANAGE_GROUP_RIGHTS: 5048,
  TWO_FACTORS_AUTHENTICATION: 5049,
  AIRCRAFT_LOCK: 5051,
  MANAGE_NOTIFICATION: 5052,

  // Client
  BASE_LOCATION: 22978,
  CLIENT_AIRCRAFT_LOCK: 22982,

  // Third Party
  MANAGE_USER_GROUPS: 22969,
  CLIENT_TWO_FACTOR_AUTHENTICATION: 22998,
} as const;

export const CsRepairPermissionLinks = {
  REPAIR_AIRCRAFT: 4080,
  ENGINE_REPAIR: 4105,
  LANDING_GEAR_REPAIR: 4123,
  APU_REPAIR: 4139,
  PROPELLER_REPAIR: 4155,
} as const;

export type UserLinkLabel = keyof typeof UserPermissionLinks;

export const TabMenuLinks = {
  // AIRCRAFT TAB PERMISSION IDS
  'Aircraft Fleet Status': '10024',
  'Aircraft Utilisation': '216',
  'Aircraft Approval': '23010',

  // LANDING GEAR TAB PERMISSION IDS
  'Landing Gear Fleet Status': '88',
  'Landing Gear Sub-Assembly Fleet': '134',
  'Gears On Ground': '89',
  'Landing Gear Utilisation': '218',

  // APU TAB PERMISSION IDS
  'APU Fleet Status': '74',
  'APUs Not Installed': '75',
  'APU Move Management': '77',
  'APU Utilisation': '219',

  // ENGINE TAB PERMISSION IDS
  'Engine Fleet Status': '58',
  'Engine Module Fleet': '120',
  'Engine Stand Type': '23027',
  'Engines Off Wing': '61',
  'Engine Move Management': '62',
  'Engine Utilisation': '217',

  // PROPELLER TAB PERMISSION IDS
  'Propeller Fleet Status': '210',
  'Propeller Off Wing': '211',
  'Propeller Utilisation': '220',

  // THRUST TAB PERMISSION IDS
  'Thrust Reverser Fleet Status': '91',
  'Thrust Reverser Not Installed': '92',
  'Thrust Reverser Utilisation': '144',

  'manage-by-document-type': '23011',
  'manage-by-asset': '129',
  'manage-by-date': '130',
  'Goods In': '68',
  'Manage Component': '23018',

  // REPAIR CENTRE TAB PERMISSION IDS
  'Aircraft Repair Mapper': 'TODO',
  'Engine Repair Mapper': 'TODO',
  'APU Repair Mapper': 'TODO',
  'Landing Gear Repair Mapper': 'TODO',
  'Thrust Reverser Repair Mapper': 'TODO',
  'Propeller Repair Mapper': 'TODO',
} as const;

export enum FLEET_LINK_MODULES {
  MCC = 'Maintenance Control Centre',
  FLY_BOOK = 'flylog Book Centre',
}

export const MhPermissionLinks = {
  'aircraft-centre': 71,
  'engine-centre': 84,
  'landing-gear-centre': 87,
  'apu-centre': 85,
  'thrust-reverser-centre': 86,
  'propeller-centre': 212,
};

export const MMD_PERMISSION_LINKS = {
  'manage-master-documentation': 64,
};

export const MccDateControlPermissionLinks = {
  'Manage Process': 1122,
  'Manage Work Status': 1123,
  'Manage Range': 1124,
  'Manage Reference Statuses': 1125,
  'Manage flysearch': 1126,
  'Manage Email Templates': 1127,
};

export const MrControlPermissionLinks = {
  'Manage Work Status List': 1251,
  'Manage Process': 1252,
  'Manage Work Status': 1253,
  'Manage Range': 1254,
  'Manage Reference Statuses': 1255,
  'Manage flysearch': 1256,
  'Manage Email Templates': 1257,
  'Manage Document Groups': 1258,
  'Manage Date Settings': 1259,
};

export const CmControlPermissionLinks = {
  'Manage Status List': 1212,
  'Manage Work Status List': 1213,
  'Column Management': 1214,
  'CSV Management': 1215,
  'flysearch Management': 1216,
};

export const CsControlPermissionLinks = {
  'Compile Files': 6001,
  'Manage flyprint Headers': 6002,
  'Return Conditions': 6003,
  'Column Management': 6004,
  'flysearch Management': 6005,
  'Manage Document Groups': 6006,
  'CSV Management': 6007,
  'Manage Status List': 6008,
  'flysign Status Management': 6009,
  'Flight Time/Cycles Calculator': 6010,
};

export const MidControlPermissionLinks = {
  'Compile Files': 5011,
};

export enum MID_TABLE_MENU_IDS {
  EDIT_TABLE = 'EDIT_TABLE',
  UPLOAD = 'UPLOAD',
}

export const MID_THIRD_PARTY_PERM = {
  'Compile Files': 2,
  'Communication History': 1,
};

export const MID_THIRD_PARTY_MENU_PERM = [
  MID_TABLE_MENU_IDS.EDIT_TABLE,
  MID_TABLE_MENU_IDS.UPLOAD,
];

export const CsPermissionLinks = {
  'Compile Files': 4010,
};

export const MccDocStatusPermissionLinks = {
  REJECT: 1139,
  APPROVED: 1140,
  QUERIED: 1141,
  REVIEWED: 1142,
  UNMARKED: 1143,
};

export const MccDocumentPermissionLinks = {
  manageByAsset: {
    main: 129,
    document: 5119,
  },
  manageByDate: {
    main: 130,
    document: 5120,
  },
};

export enum MCC_DOCUMENT_ACTIONS {
  REVIEW_MANAGEMENT = 'REVIEW_MANAGEMENT',
  DOCUMENT_HISTORY = 'DOCUMENT_HISTORY',
  BASIC_FUNCTIONS = 'BASIC_FUNCTIONS',
  ADVANCED_FUNCTIONS = 'ADVANCED_FUNCTIONS',
  WORKPACK_FUNCTIONS = 'WORKPACK_FUNCTIONS',
}

export const MccLinkPermissions = {
  MCC_DATE_REPORTS: 9003,
  MCC_ASSET_LISTING_REPORTS: 9003,
  MCC_MANAGE_ROWS_REPORTS: 9003,
  MCC_DATE_LISTING_REPORTS: 9003,
};

export const FlydocTemplatePermissions = {
  SHOW_TEMPLATE_FIELDS_TO: 5034,
  MANAGE_FLYSIGN: 5035,
  MANAGE_FIELD_MAPPING: 5036,
  PROCESS_LINK: 5037,
  FLYDOC_TEMPLATE: 94,
};

export const MccAssetControlPermissionLinks = {
  'Upload Workpack': 1111,
  'Column Management': 1112,
  'Manage Work Status List': 1113,
  'Manage Listing Work Status List': 1114,
  'Manage Workpack Mapping': 1115,
  'Manage Status List': 1116,
  'General Components Upload': 1117,
  'Manage Document Groups': 1118,
  'Manage Email Templates': 1119,
  'General Components Columns': 1120,
};

export const FleetCentreTypePermissionLinks = {
  AIRCRAFT_CENTRE: 10024,
  AIRCRAFT: 10024,
  ENGINE_CENTRE: 58,
  ENGINE: 58,
  ENGINE_MODULE: 120,
  APU_CENTRE: 74,
  APU: 74,
  LANDING_GEAR_MODULE: 18,
  LANDING_GEAR_CENTRE: 88,
  LANDING_GEAR: 88,
  THRUST_REVERSER: 91,
  PROPELLER: 210,
  COMPONENT_CENTRE: 23014,
  MMD: 64,
  INTERNAL: 65,
};

export interface AirlineMccResponseType {
  id: string;
  clientId: string;
  docType: string;
  mccViewSetting: boolean;
  doctypes: {
    name: string;
    type: string;
  };
  moveToAssetByReferenceNumber: boolean;
  assetAlphabetOrder: boolean;
}

export interface FleetThirdPartyPermissionLinks {
  CS?: number;
  MH?: number;
}
